/**
 * App-wide events: past-tense facts, emitted where they happen. Producers don't
 * know their listeners. Delivery is synchronous and fire-and-forget.
 */

export type MapView = 'maps' | 'story' | 'other';

export type AoiSource = 'drawn' | 'saved' | 'boundary' | 'agent';

/**
 * `area`: left for its project. `boundary`: cleared a boundary focus, still in the site.
 * `replaced`: another area took its place.
 */
export type AoiExitKind = 'area' | 'boundary' | 'replaced';

export type TourId = 'restoration';

export type OnboardingOutcome = 'completed' | 'deferred' | 'not_needed';

export type OnboardingTrigger = 'auto';

export type OnboardingCloseReason = 'saved' | 'dismissed' | 'navigated';

/** Where the contact form was opened from. Mirrors the backend's `ContactRequest.Source`. */
export type ContactSource = 'locked_chapter' | 'boundary_files_menu' | 'request_files' | 'help';

/** What a general contact request is about. Mirrors the backend's `ContactRequest.Topic`. */
export type ContactTopic =
  | 'premium_insights'
  | 'boundary_files'
  | 'platform_help'
  | 'pricing'
  | 'other';

/** Where a Stage 2 CTA sits. Analytics only: the request itself is always `stage2_request`. */
export type StageTwoPlacement = 'upgrade_sidebar' | 'upgrade_story_panel' | 'methodology';

export interface AppEventMap {
  'aoi.draw_started': undefined;
  /** Draw tool disarmed, or an unsaved polygon discarded. */
  'aoi.draw_cancelled': undefined;
  /** The first point of a new shape was placed. */
  'aoi.first_point_placed': undefined;
  /** A polygon was closed on the map. */
  'aoi.draw_completed': undefined;
  'aoi.entered': { source: AoiSource };
  /** A drawn area has no data from any product; it was opened, not saved. */
  'aoi.not_covered': undefined;
  'aoi.exited': { kind: AoiExitKind };
  'view.changed': { from: MapView; to: MapView };
  'basemap.selected': { basemap: string };
  /** The tools rail's Recenter: frame the project, site or area in view again. */
  'map.recenter_requested': undefined;
  /** A layer's value histogram mounted in the side panel. */
  'layer.histogram_shown': undefined;
  'layer.histogram_hidden': undefined;
  /** A value range was picked on a layer's histogram. */
  'layer.value_range_set': undefined;
  /** The user picked an insight in the story. */
  'story.insight_selected': undefined;
  /** May repeat across pages. */
  'onboarding.resolved': { outcome: OnboardingOutcome };
  'onboarding.modal_opened': { trigger: OnboardingTrigger };
  /** `completed` is the server's verdict after a save; otherwise false. */
  'onboarding.modal_closed': { reason: OnboardingCloseReason; completed: boolean };
  'help.tour_requested': { tour: TourId };
  'tour.started': { tour: TourId };
  'tour.step_completed': { tour: TourId; step: string };
  'tour.skipped': { tour: TourId; step: string };
  'tour.completed': { tour: TourId };
  /** The tour reached the steps it shows inside the demo area. */
  'tour.demo_area_requested': undefined;
  /** The tour left those steps, by Back, Finish or Skip. */
  'tour.demo_area_closed': undefined;
  /** Opens the contact form, pre-filled with the topic, chapter id and area it was asked about. */
  'contact.requested': {
    source: ContactSource;
    topic?: ContactTopic;
    chapter?: string;
    areaId?: number;
  };
  /** Opens the Stage 2 request form. */
  'stage_two.requested': { source: StageTwoPlacement };
  /** The server stored the request. */
  'contact.submitted': { source: ContactSource | 'stage2_request' };
}

export type AppEventType = keyof AppEventMap;

export type AppEvent = {
  [K in AppEventType]: { type: K; payload: AppEventMap[K] };
}[AppEventType];

type Listener = (event: AppEvent) => void;

const listeners = new Set<Listener>();

export const appEvents = {
  emit<K extends AppEventType>(
    type: K,
    ...[payload]: AppEventMap[K] extends undefined ? [] : [AppEventMap[K]]
  ): void {
    const event = { type, payload } as AppEvent;
    [...listeners].forEach((listener) => {
      try {
        listener(event);
      } catch (error) {
        // A failing listener must not break the emitter.
        console.error(`appEvents listener failed on ${type}`, error);
      }
    });
  },

  /** Returns an unsubscribe function. */
  subscribe(listener: Listener): () => void {
    listeners.add(listener);
    return () => {
      listeners.delete(listener);
    };
  },
};
